import React from 'react';
import { createPortal } from 'react-dom';
import { AlertCircleIcon, MailCheckIcon, XIcon } from 'lucide-react';
import './staffApplication.generated.css';

// Step 2 of the public staff signup: the applicant types the 4-digit code emailed to the
// address on the form. Purely presentational — the timers, the service calls and every
// errorCode decision live in addCounselor.js, so this renders whatever it is handed.
// Also used by the director "add staff" OTP (useStaffAddOtp) via the optional copy props.
//
// Not react-otp-input, which the older OTP screens use: its styles are Bootstrap rules in
// assets/css/main.css and would land unstyled on this Tailwind-only page.

const formatSeconds = seconds => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const EmailConfirmationModal = ({
    email,
    otp,
    onOtpChange,
    error,
    expirySeconds,
    cooldownSeconds,
    mustResend,
    sending,
    submitting,
    onResend,
    onConfirm,
    onClose,
    title = 'Confirm your email address',
    description = 'Enter it below to send your application to the director.',
    confirmLabel = 'Confirm & submit',
    footnote = 'Close this to go back and edit your answers — nothing is submitted until you confirm the code.',
    zIndex,
}) => {
    // mustResend means the code in hand can never succeed (errorCode 75 or 20), so the
    // cooldown is irrelevant and resending becomes the only thing worth doing.
    const resendDisabled = sending || (cooldownSeconds > 0 && !mustResend);
    const confirmDisabled = submitting || sending || mustResend || otp.length !== 4;

    const resendLabel = sending
        ? 'Sending…'
        : cooldownSeconds > 0 && !mustResend
            ? `Resend in ${formatSeconds(cooldownSeconds)}`
            : 'Resend code';

    const modal = (
        <div
            className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-gray-900/50 tw-p-4"
            style={zIndex ? { zIndex } : undefined}
            onClick={submitting ? undefined : onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="staff-otp-heading"
                onClick={event => event.stopPropagation()}
                className="tw-relative tw-w-full tw-max-w-md tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-xl sm:tw-p-7">
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="tw-absolute tw-right-4 tw-top-4 tw-rounded-lg tw-p-1 tw-text-gray-400 tw-transition hover:tw-bg-sand-100 hover:tw-text-gray-600">
                    <XIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
                </button>

                <span className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl tw-bg-brand-50 tw-text-brand-700">
                    <MailCheckIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
                </span>
                <h2 id="staff-otp-heading" className="tw-mt-4 tw-text-lg tw-font-bold tw-text-gray-900">
                    {title}
                </h2>
                <p className="tw-mt-1.5 tw-text-sm tw-text-gray-600">
                    We sent a 4-digit code to <span className="tw-font-semibold tw-text-gray-900">{email}</span>.
                    {description}
                </p>

                <label htmlFor="staff-otp-input" className="tw-mt-5 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-500">
                    Confirmation code
                </label>
                <input
                    id="staff-otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={4}
                    value={otp}
                    onChange={event => onOtpChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
                    onKeyDown={event => { if (event.key === 'Enter' && !confirmDisabled) onConfirm(); }}
                    placeholder="0000"
                    className={`tw-mt-2 tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-3.5 tw-py-3 tw-text-center tw-text-2xl tw-font-bold tw-tracking-[0.6em] tw-text-gray-900 placeholder:tw-font-normal placeholder:tw-text-gray-300 tw-transition focus:tw-outline-none focus:tw-ring-2 ${
                        error
                            ? 'tw-border-brand-500 focus:tw-border-brand-500 focus:tw-ring-brand-500/20'
                            : 'tw-border-sand-300 focus:tw-border-brand-500 focus:tw-ring-brand-500/20'
                    }`} />

                {error ? (
                    <p role="alert" className="tw-mt-2 tw-flex tw-items-start tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-brand-700">
                        <AlertCircleIcon className="tw-mt-px tw-h-4 tw-w-4 tw-shrink-0" aria-hidden="true" />
                        <span>{error}</span>
                    </p>
                ) : null}

                <p className="tw-mt-2 tw-text-xs tw-text-gray-500">
                    {expirySeconds > 0 ? `Code expires in ${formatSeconds(expirySeconds)}` : 'This code has expired.'}
                </p>

                <div className="tw-mt-5 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <button
                        type="button"
                        onClick={onResend}
                        disabled={resendDisabled}
                        className={
                            mustResend
                                ? 'tw-rounded-lg tw-bg-brand-700 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-transition hover:tw-bg-brand-800 disabled:tw-opacity-60'
                                : 'tw-rounded-lg tw-border tw-border-sand-300 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-gray-700 tw-transition hover:tw-bg-sand-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60'
                        }>
                        {resendLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirmDisabled}
                        className={
                            mustResend
                                ? 'tw-ml-auto tw-rounded-lg tw-border tw-border-sand-300 tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-gray-700 disabled:tw-cursor-not-allowed disabled:tw-opacity-60'
                                : 'tw-ml-auto tw-rounded-lg tw-bg-brand-700 tw-px-5 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-white tw-shadow-sm tw-transition hover:tw-bg-brand-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-60'
                        }>
                        {submitting ? 'Submitting…' : confirmLabel}
                    </button>
                </div>

                <p className="tw-mt-4 tw-text-xs tw-text-gray-500">
                    {footnote}
                </p>
            </div>
        </div>
    );

    // With zIndex, render into <body>: a parent stacking context (e.g. .body_container z-index 1)
    // would otherwise cap it below bootstrap modals.
    return zIndex ? createPortal(modal, document.body) : modal;
};

export default EmailConfirmationModal;
